import type {
  AddToCalendarDelegate,
  DocumentDownloadDelegate,
  ExternalUrlDelegate,
  ExternalUrlOpenHandle,
  PlatformDelegate,
} from '@/features/shell/platform/types'

class DefaultExternalUrlDelegate implements ExternalUrlDelegate {
  createHandle(): ExternalUrlOpenHandle {
    const newWindow = window.open('about:blank', '_blank')
    return {
      open(url: string) {
        if (!newWindow) {
          return
        }

        newWindow.location.href = url
      },
      abort() {
        newWindow?.close()
      },
    }
  }
}

class DefaultDocumentDownloadDelegate implements DocumentDownloadDelegate {
  /**
   * On the web we require the use of form submission.
   */
  readonly requiresFormSubmission = true

  /** @inheritDoc */
  download() {
    // Not used due to using form submission
  }
}

class DefaultAddToCalendarDelegate implements AddToCalendarDelegate {
  addToCalendar(icsFile: File): void {
    // Create a URL for the file
    const url = URL.createObjectURL(icsFile)

    // Create a temporary link element
    const link = document.createElement('a')
    link.href = url
    link.download = 'calendar.ics'

    // Click it bro!
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}

/**
 * Default delegate for the web.
 */
export class DefaultPlatformDelegate implements PlatformDelegate {
  static readonly instance = new DefaultPlatformDelegate()

  private readonly _externalUrlDelegate = new DefaultExternalUrlDelegate()
  private readonly _documentDownloadDelegate =
    new DefaultDocumentDownloadDelegate()
  private readonly _addToCalendarDelegate = new DefaultAddToCalendarDelegate()

  private constructor() {
    this.getExternalUrlDelegate = this.getExternalUrlDelegate.bind(this)
    this.getDocumentDownloadDelegate =
      this.getDocumentDownloadDelegate.bind(this)
    this.getAddToCalendarDelegate = this.getAddToCalendarDelegate.bind(this)
  }

  getExternalUrlDelegate() {
    return this._externalUrlDelegate
  }

  getDocumentDownloadDelegate() {
    return this._documentDownloadDelegate
  }

  getAddToCalendarDelegate() {
    return this._addToCalendarDelegate
  }
}
